<template>
  <div class="inline-message-widget card p-3 mt-4">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h6 class="mb-0 font-weight-bold">
        Messages{{ recipientName ? ` with ${recipientName}` : "" }}
      </h6>
      <router-link
        v-if="conversation"
        :to="{ path: '/messages', query: { conversationId: conversation.id } }"
        class="btn btn-sm btn-link p-0"
      >
        View full conversation →
      </router-link>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-3">
      <span class="text-muted small">Loading...</span>
    </div>

    <!-- No existing conversation -->
    <div v-else-if="!conversation" class="text-center py-3">
      <p v-if="readOnly" class="text-muted small mb-0">No messages found</p>
      <template v-else>
        <p class="text-muted small mb-3">
          No messages with this candidate yet.
        </p>
        <default-button
          :labelText="'Send a message'"
          @click="openNewMessageModal"
        />
      </template>
    </div>

    <!-- Existing conversation -->
    <div v-else class="conversation-body">
      <div class="messages-preview mb-3">
        <div
          v-if="recentMessages.length === 0"
          class="text-center text-muted small py-2"
        >
          {{
            readOnly
              ? "No messages found"
              : "No messages yet. Send the first one!"
          }}
        </div>
        <div
          v-for="message in recentMessages"
          :key="message.id"
          class="message-row d-flex mb-2"
          :class="
            isMine(message) ? 'justify-content-end' : 'justify-content-start'
          "
        >
          <div
            class="message-bubble p-2 px-3"
            :class="isMine(message) ? 'bubble-sent' : 'bubble-received'"
          >
            <p class="mb-1 small">{{ message.body }}</p>
            <small class="timestamp">{{
              formatDate(message.created_at)
            }}</small>
          </div>
        </div>
      </div>

      <!-- Quick reply -->
      <form @submit.prevent="sendReply" class="d-flex">
        <textarea
          v-model="replyBody"
          class="form-control form-control-sm me-2"
          rows="2"
          placeholder="Type a message..."
          :disabled="sending"
          @keydown.enter.exact.prevent="sendReply"
        />
        <button
          type="submit"
          class="btn btn-primary btn-sm align-self-end flex-shrink-0"
          :disabled="!replyBody.trim() || sending"
        >
          {{ sending ? "Sending…" : "Send" }}
        </button>
      </form>
    </div>

    <!-- New message modal -->
    <default-modal
      id="inline-new-message-modal"
      title="New Message"
      confirm-label="Send"
      description=""
      :show-cancel="true"
      :disable-confirm-button="!newMessageBody.trim() || sendingNew"
      @onConfirm="submitNewMessage"
      @onCancel="resetNewMessage"
      @onClosed="resetNewMessage"
    >
      <template #input>
        <div class="form-group">
          <label class="font-weight-bold">To</label>
          <input
            type="text"
            class="form-control"
            :value="recipientName || jobSeekerId"
            disabled
          />
        </div>
        <div class="form-group mb-0">
          <label class="font-weight-bold">Message</label>
          <textarea
            v-model="newMessageBody"
            class="form-control"
            rows="4"
            placeholder="Write your message…"
            :disabled="sendingNew"
          />
          <small v-if="newMessageError" class="text-danger">{{
            newMessageError
          }}</small>
        </div>
      </template>
    </default-modal>
  </div>
</template>

<script>
import moment from "moment"
import MessagingApi from "@/api/messaging"
import DefaultButton from "@/components/DefaultButton"

export default {
  name: "InlineMessageWidget",
  components: { DefaultButton },

  props: {
    jobSeekerId: {
      type: String,
      required: true,
    },
    recipientName: {
      type: String,
      default: "",
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: true,
      conversation: null,
      replyBody: "",
      sending: false,
      newMessageBody: "",
      sendingNew: false,
      newMessageError: "",
    }
  },

  computed: {
    recentMessages() {
      if (!this.conversation?.messages) return []
      return this.conversation.messages.slice(-10)
    },
    currentUserId() {
      return this.$store.state.user._id
    },
  },

  async mounted() {
    try {
      const { data } = await MessagingApi.getThreadWith(this.jobSeekerId)
      this.conversation = {
        ...data.conversation,
        messages: data.messages || [],
      }
    } catch {
      // 404 or no thread yet — conversation stays null, show "Send a message" button
    } finally {
      this.loading = false
    }
  },

  methods: {
    isMine(message) {
      return message.senderId === this.currentUserId
    },

    formatDate(date) {
      if (!date) return ""
      return moment(date).format("MMM D, h:mm a")
    },

    openNewMessageModal() {
      this.$bvModal.show("inline-new-message-modal")
    },

    resetNewMessage() {
      this.newMessageBody = ""
      this.newMessageError = ""
      this.sendingNew = false
    },

    async submitNewMessage() {
      if (!this.newMessageBody.trim()) {
        this.newMessageError = "Message is required."
        return
      }
      this.newMessageError = ""
      this.sendingNew = true
      try {
        const { data: conversation } = await MessagingApi.getOrCreateThread(
          this.jobSeekerId
        )
        await MessagingApi.sendMessage(
          conversation.id,
          this.newMessageBody.trim()
        )
        this.$bvModal.hide("inline-new-message-modal")
        this.$router.push({
          path: "/messages",
          query: { conversationId: conversation.id },
        })
      } catch {
        this.newMessageError = "Something went wrong. Please try again."
      } finally {
        this.sendingNew = false
      }
    },

    async sendReply() {
      const body = this.replyBody.trim()
      if (!body || this.sending) return
      this.sending = true
      try {
        const { data: message } = await MessagingApi.sendMessage(
          this.conversation.id,
          body
        )
        this.replyBody = ""
        if (!this.conversation.messages) {
          this.conversation.messages = []
        }
        this.conversation.messages.push(message)
      } catch {
        // swallow — parent can show toast if needed
      } finally {
        this.sending = false
      }
    },
  },
}
</script>

<style scoped>
.conversation-body {
  width: 100%;
}

.messages-preview {
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  scroll-behavior: smooth;
}

.message-bubble {
  max-width: 75%;
  border-radius: 1rem;
  word-break: break-word;
}

.bubble-sent {
  background-color: #4779c9;
  color: #fff;
  border-bottom-right-radius: 0.25rem;
}

.bubble-sent p {
  color: #fff;
}

.bubble-received {
  background-color: #f0f0f0;
  color: #333;
  border-bottom-left-radius: 0.25rem;
}

.bubble-sent .timestamp {
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.65rem;
  display: block;
  text-align: right;
}

.bubble-received .timestamp {
  color: #999;
  font-size: 0.65rem;
  display: block;
}

.message-input textarea {
  resize: none;
}
</style>
